import { useEffect, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import { Spinner } from "@/components/Loader";
import { affGet, AFF_API_BASE, getAffiliateToken } from "@/lib/affiliateApi";
import { fmtMoney } from "@/lib/format";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

export default function Payouts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await affGet("/affiliate/payouts", { auth: true });
        if (!cancelled) setData(res || {});
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const next = data?.nextPayout;
  const past = data?.pastPayouts || [];

  const downloadTaxStatement = async () => {
    setDownloading(true);
    try {
      const token = getAffiliateToken();
      const res = await fetch(`${AFF_API_BASE}/affiliate/tax-statement`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Could not generate statement.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "voya9-affiliate-1099-NEC.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast("Tax statement is not available yet for the current period.", "info");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div data-testid="affiliate-payouts" style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 20px 64px" }}>
      <Eyebrow color={C.peachDeep}>★ PAYOUTS</Eyebrow>
      <Headline before="WHAT YOU'VE" italic="been paid." size={48} className="mt-4" />

      {error && (
        <div style={{ marginTop: 22, padding: "14px 16px", background: C.bone, borderLeft: `3px solid ${C.coral}`, fontSize: 13, color: C.warmGray }}>
          Couldn't load payouts just now. Refresh in a moment.
        </div>
      )}

      {/* Next payout */}
      <section data-testid="payouts-next" style={{ marginTop: 32, padding: 28, background: C.peach, color: C.peachDark }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em" }}>★ NEXT PAYOUT</div>
        {next ? (
          <>
            <div style={{ marginTop: 10, fontSize: 40, fontWeight: 800, color: C.ink, letterSpacing: "-0.03em" }}>
              {fmtMoney(next.amount || 0)}
            </div>
            <div style={{ marginTop: 4, fontSize: 14, color: C.peachDark }}>
              Scheduled for {next.scheduledFor || "the 15th"} via {next.method || "your payout method"}
            </div>
            {next.breakdown && (
              <div style={{ marginTop: 18, padding: 18, background: "rgba(15,14,13,0.06)", borderLeft: `3px solid ${C.ink}` }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: C.ink, marginBottom: 8 }}>BREAKDOWN</div>
                {(next.breakdown || []).map((b, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "4px 0" }}>
                    <span style={{ color: C.peachDark }}>{b.label}</span>
                    <span style={{ color: C.ink, fontWeight: 700 }}>{fmtMoney(b.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{ marginTop: 12, fontSize: 14, color: C.peachDark, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            No upcoming payout. Earn at least $50 in a month and you'll see one scheduled here.
          </div>
        )}
      </section>

      {/* Past payouts */}
      <section style={{ marginTop: 48 }}>
        <Eyebrow>★ PAST PAYOUTS</Eyebrow>
        <div style={{ marginTop: 18, background: C.pure, border: `1px solid ${C.warmGray3}` }}>
          {past.length === 0 ? (
            <div data-testid="payouts-empty" style={{ padding: 28, fontSize: 14, color: C.warmGray, textAlign: "center", fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
              No prior payouts.
            </div>
          ) : (
            past.map((p, i) => (
              <div
                key={p.id || i}
                data-testid={`payout-${i}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1.6fr 1fr 1fr",
                  gap: 12,
                  padding: "16px 18px",
                  borderBottom: `1px solid ${C.warmGray3}`,
                  alignItems: "center",
                  fontSize: 13,
                }}
                className="!grid-cols-[1fr_1fr] md:!grid-cols-[1fr_1.6fr_1fr_1fr]"
              >
                <div style={{ fontWeight: 700, color: C.ink }}>{p.date || "—"}</div>
                <div className="hidden md:block" style={{ color: C.warmGray }}>
                  {p.method || "Stripe Connect"}
                </div>
                <div style={{ fontWeight: 800, color: C.ink, fontSize: 16 }}>{fmtMoney(p.amount || 0)}</div>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", color: p.status === "paid" ? C.sageDark : C.warmGray }}>
                  {(p.status || "PAID").toUpperCase()}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <div style={{ marginTop: 32 }}>
        <Button variant="ghost" testId="download-tax-stmt" onClick={downloadTaxStatement} disabled={downloading}>
          {downloading ? "PREPARING…" : "DOWNLOAD TAX STATEMENT (1099-NEC) →"}
        </Button>
      </div>
    </div>
  );
}
